import React, { useCallback, useState, useMemo, useRef, useEffect, memo } from "react";
import { useSelector } from "react-redux";
import type { TypedUseSelectorHook } from "react-redux";
import type { RootState } from "../store";
import {
    Card,
    CardContent,
    Divider,
    TextField,
    IconButton,
    Accordion,
    Dialog,
    Chip,
    AccordionSummary,
    AccordionDetails,
    lighten,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Select,
    useMediaQuery,
} from "@mui/material";
import { Favorite, FavoriteBorder, ChatBubbleOutline, Send, ExpandMore, Close } from "@mui/icons-material";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import type { IPost } from "../types/post";
import axiosService from "../utils/axios";
import Loading from "./Loading";
import Theme from "../utils/theme";
import type { ISavedRecipes } from "../types/recipe";
import Comments from "./Comments";
import ImageBox from "./ImageBox";
import UserLink from "./default/UserLink";
import { openRecipeModal as openRecipeModalHelper, visibilityLabels, visibilityOptions } from "../utils/helpers/recipeHelper";
import { substringInUserTags } from "../utils/helpers/postHelper";
import { useRefreshPosts } from "../hooks/useRefreshPosts";
import SavedRecipe from "./SavedRecipe";
import type { IComment } from "../types/comment";
import CatalogListDialog from "./CatalogListDialog";
import ImageStack from "./ImageStack";
import { updateImageBoxData } from "../utils/helpers/imageHelper";
import type { IUserSettingsVisibility } from "../types/users";
import { scrollbarCss } from "../utils/themeScrollbar";

// *** Component specific types *** //
interface IRecipePostCardProps {
    post: IPost;
    setPost?: React.Dispatch<React.SetStateAction<IPost>>;
    type?: "card" | "preview";
    followedOnly?: boolean;
}

// *** Memoized Components *** //
const MemoizedImageStack = memo(ImageStack);

// *** Hooks *** //
const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;

// ***** Main Component ***** //
const PostCard: React.FC<IRecipePostCardProps> = ({ post, type = "card", setPost, followedOnly }) => {
    // *** Root States *** //
    const authState = useSelector((state: RootState) => state.auth);
    const username = authState.account?.username;

    const isUser = post.user.username === username;

    const themeState = useTypedSelector((state) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);

    // *** Component States *** //
    const [localPost, setLocalPost] = useState<IPost>(post);
    const [showComments, setShowComments] = useState(type === "card");
    const [newComment, setNewComment] = useState("");
    const [loading, setLoading] = useState(false);
    const [openImageModal, setOpenImageModal] = useState(false);
    const [openRecipeModal, setOpenRecipeModal] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [currentRecipe, setCurrentRecipe] = useState<ISavedRecipes | null>(null);
    const [openCatalogListDialogModal, setOpenCatalogListDialogModal] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [visibility, setVisibility] = useState<IUserSettingsVisibility | undefined>(post.visibility);

    const isXs = useMediaQuery(theme.breakpoints.down("sm"));

    // *** Component Constants *** //
    const cardContentRef = useRef<HTMLDivElement>(null);

    const checkIsOverflowing = () => {
        // Check to see if the post card is overflowing //
        const el = cardContentRef.current;
        if (el) {
            setIsOverflowing(el.scrollHeight > el.clientHeight);
        }
    };

    // *** Use Effects *** //
    useEffect(() => {
        checkIsOverflowing();
    }, [localPost, type]);

    // *** Component Helpers *** //

    const handleVisibilityChange = useCallback(
        (vis: IUserSettingsVisibility) => {
            setVisibility(vis);

            axiosService.post(`${process.env.REACT_APP_API_URL}/posts/updateVisibility`, { visibility: vis, postId: post._id });
        },
        [post._id]
    );
    // Toggles the panel for showing comments //
    const toggleComments = useCallback(() => setShowComments((prev) => !prev), []);

    // Refreshes global state's posts //
    const refreshPosts = useRefreshPosts(setLoading);

    // Comment submit on-click //
    const handleCommentSubmit = useCallback(async () => {
        // If comment not trimmed or already loading an API Call, then return //
        if (!newComment.trim() || loading) return;

        setLoading(true);

        const res = await axiosService
            .post(`${process.env.REACT_APP_API_URL}/posts/comment/create`, {
                content: newComment,
                postId: localPost["_id"],
            })
            .finally(() => {
                setLoading(false);
                setNewComment("");
            });

        const resComment = {
            _id: res.data.body._id,
            user: res.data.body.user,
            likes: res.data.body.likes,
            createdAt: res.data.body.createdAt,
            content: res.data.body.content,
        } as IComment;

        // Set the local post's comments //
        const next = { ...localPost, comments: [...localPost.comments, resComment] };

        setLocalPost(next);
        if (setPost) setPost(next);

        // Refresh the global state's comments //
        refreshPosts({
            username: authState.account?.username,
            targetUsername: authState.account?.username,
            followedOnly: followedOnly,
            onDone: () => {}, //Do nothing for now
        });
    }, [newComment, loading, localPost, setPost, refreshPosts, authState.account?.username, followedOnly]);

    // Open preview version of post (dialog) //
    const handlePreviewCommentClick = useCallback(() => {
        setOpenCatalogListDialogModal(true);
    }, []);

    // Open recipe view modal //
    const handleRecipeClick = useCallback((recipeId: string) => {
        openRecipeModalHelper(recipeId, setCurrentRecipe, setOpenRecipeModal);
    }, []);

    // Like a post //
    const handlePostLike = useCallback(() => {
        // Only logged in users can like posts //
        if (!username) return;

        // If the username is in the list of likes, then filter it out. Otherwise, add it. //
        // Updating locally //
        setLocalPost((prev) => {
            const next = {
                ...prev,
                likes: prev.likes.includes(username) ? prev.likes.filter((u) => u !== username) : [...prev.likes, username],
            };

            // If there is a parent's set function, use it to set parent local data //
            if (setPost) {
                setPost(next);
            }

            return next;
        });

        // Updating backend //
        axiosService.post(`${process.env.REACT_APP_API_URL}/posts/updateLikes`, { postId: localPost._id });
    }, [localPost, setPost, username]);

    // Set a post's image's data //
    const setLocalPostImages = useCallback(
        (type: "likes" | "commentText" | "commentLike", comment?: IComment, commentId?: string) => {
            if (!username || (type === "commentText" && !comment) || (type === "commentLike" && !commentId)) return;

            setLocalPost((prev) => {
                const next = {
                    ...prev,
                    content: {
                        ...prev.content,
                        images: updateImageBoxData(prev.content.images, currentImageIndex, type, username, comment, commentId),
                    },
                };

                // If there is a parent's set function, use it to set parent local data //
                if (setPost) {
                    setPost(next);
                }

                return next;
            });
        },
        [currentImageIndex, setPost, username]
    );

    // Render the recipes attached to the post in default view //
    const renderedPostRecipes = useMemo(
        () => (
            <Box sx={{ my: 3, display: "flex", flexWrap: "wrap" }} gap={1}>
                {localPost.recipes.map((recipe, index) => (
                    <Chip label={recipe.name} key={index} onClick={() => handleRecipeClick(recipe._id)} />
                ))}
            </Box>
        ),
        [localPost, handleRecipeClick]
    );

    // Render recipes attached to the post in accordian view //
    const renderedPostRecipesAccordian = useMemo(
        () =>
            localPost.recipes.map((recipe, index) => (
                <Box key={recipe._id} sx={{ display: "flex", alignItems: "center", mb: index !== localPost.recipes.length - 1 ? 1 : 0 }}>
                    <Chip label={recipe.name} onClick={() => handleRecipeClick(recipe._id)} />
                </Box>
            )),
        [localPost, handleRecipeClick]
    );

    // Render tags associated to post//
    // Does text parsing to figure out what is a tag //
    const renderTags = (text: string) => {
        // Tags are classified as # for categories and @ for users //
        const rx = /(?<!\S)([#@])([A-Za-z0-9_]+)\b/g;

        const parts: React.ReactNode[] = [];
        let last = 0;
        let m: RegExpExecArray | null;

        while ((m = rx.exec(text)) !== null) {
            const full = m[0];
            const symbol = m[1];

            if (m.index > last) parts.push(text.slice(last, m.index));

            // Setting the tag and onClick function depending on tag //
            if (symbol === "#") {
                parts.push(
                    <Typography
                        key={`tag-${m.index}`}
                        component="span"
                        sx={{ cursor: "pointer", color: "primary.main", font: "inherit" }}
                        onClick={() => {}} // TODO: Implement linking to a tag through search if clicked on.
                    >
                        {full}
                    </Typography>
                );
                last = m.index + full.length;
            } else if (substringInUserTags(localPost.userTags, full)) {
                parts.push(
                    <Typography
                        key={`mention-${m.index}`}
                        component="a"
                        sx={{
                            cursor: "pointer",
                            color: "primary.main",
                            font: "inherit",
                            textDecoration: "none",
                            backgroundColor: lighten(theme.palette.secondary.main, 0.7),
                        }}
                        href={`/profile/${full}`}
                    >
                        {full}
                    </Typography>
                );
                last = m.index + full.length;
            } else {
                last = m.index;
            }
        }

        if (last < text.length) parts.push(text.slice(last));

        return parts;
    };

    return (
        <Card
            sx={{
                position: "relative",
                maxHeight: { xs: "none", sm: "calc(100dvh - 150px)" },
                borderRadius: 2,
                borderTop: { xs: "none", sm: "1px solid #E5E7EB" },
                borderRight: { xs: "none", sm: "1px solid #E5E7EB" },
                borderLeft: { xs: "none", sm: "1px solid #E5E7EB" },
                overflow: type === "card" ? "auto" : "hidden",
                display: "flex",
                flexDirection: "column",
                ...scrollbarCss,
            }}
        >
            {isUser && (
                <Box sx={{ position: "absolute", right: 5, top: 5 }}>
                    <Select
                        labelId="visibility-label"
                        id="visibility"
                        value={visibility}
                        label="Visibility"
                        onChange={(e) => handleVisibilityChange(e.target.value)}
                        sx={{
                            backgroundColor: lighten(theme.palette.primary.main, 0.2),
                            height: "28px",
                            color: "white",
                            fontSize: "13px",
                        }}
                        renderValue={(selectedValue) => {
                            const selectedOption = Object.values(visibilityOptions).find((option) => option.value === selectedValue);
                            return selectedOption ? selectedOption.label : "";
                        }}
                    >
                        {Object.values(visibilityOptions).map(({ value, label, Icon }) => (
                            <MenuItem
                                value={value as IUserSettingsVisibility}
                                key={value as IUserSettingsVisibility}
                                sx={{
                                    py: 1.25,
                                    "&.Mui-selected": { bgcolor: "action.selected" },
                                    "& .MuiListItemText-primary": { fontWeight: 500 },
                                }}
                            >
                                <ListItemIcon>
                                    <Icon sx={{ color: theme.palette.primary.main }} fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={label} secondary={visibilityLabels[value as keyof typeof visibilityLabels]} />
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            )}
            <Box sx={{ mt: 3, width: "100%" }}>
                {/* Post author information */}
                <Box sx={{ mx: 3 }} display="flex" alignItems="center">
                    <Avatar src={localPost.user.image ? localPost.user.image.url : ""} alt={localPost.user.username} />
                    <Box ml={2}>
                        <Typography variant="subtitle1" fontWeight="bold">
                            <UserLink username={localPost.user.username}></UserLink>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {new Date(localPost.createdAt).toLocaleString()}
                        </Typography>
                    </Box>
                </Box>

                {/* Post Content */}
                <CardContent
                    ref={cardContentRef}
                    sx={{
                        px: 3,
                        mx: 3,
                        maxHeight: type === "card" ? "" : "50dvh",
                        overflow: type === "card" ? "" : "hidden",
                        position: "relative",
                    }}
                >
                    {/* Text Content */}
                    <Typography sx={{ whiteSpace: "pre-wrap", fontSize: "1.25em", pb: 2 }}>{renderTags(localPost.content.text)}</Typography>

                    {/* Image Content */}
                    <Box
                        sx={{
                            position: "relative",
                            width: "100%",
                            maxWidth: localPost.content.images.length === 1 ? 300 : 600,
                            mx: "auto",
                            // mb: 3,
                        }}
                    >
                        {/* Image Grid */}
                        {localPost.content.images.length > 0 && (
                            <MemoizedImageStack
                                images={post.content.images}
                                currentImageIndex={currentImageIndex}
                                setCurrentImageIndex={setCurrentImageIndex}
                                setOpenImageModal={setOpenImageModal}
                            />
                        )}
                    </Box>

                    {/* Recipe Content */}
                    {localPost.recipes.length > 0 &&
                        (localPost.recipes.length > 3 ? (
                            <Box sx={{ mb: 1 }}>
                                <Accordion
                                    elevation={0}
                                    disableGutters
                                    TransitionProps={{
                                        onEntered: () => {
                                            // expanded finished
                                            requestAnimationFrame(checkIsOverflowing);
                                        },
                                        onExited: () => {
                                            // collapsed finished
                                            requestAnimationFrame(checkIsOverflowing);
                                        },
                                    }}
                                    sx={{
                                        borderRadius: 3,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        overflow: "hidden",
                                        "&:before": { display: "none" }, // remove default line
                                        boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMore />}
                                        sx={{
                                            "& .MuiAccordionSummary-content": {
                                                my: 0.5,
                                            },
                                        }}
                                    >
                                        <Typography variant="body2">{localPost.recipes.length} recipe(s)</Typography>
                                    </AccordionSummary>

                                    <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>{renderedPostRecipesAccordian}</AccordionDetails>
                                </Accordion>
                            </Box>
                        ) : (
                            renderedPostRecipes
                        ))}
                </CardContent>

                {/* Fade overlay */}
                {type === "preview" && (
                    <Box sx={{ position: "relative" }}>
                        <Box
                            sx={{
                                position: "absolute",
                                top: -24,
                                left: 0,
                                right: 0,
                                height: 24,
                                pointerEvents: "none",
                                background: "linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))",
                            }}
                        />
                        <Box
                            sx={{
                                p: 1,
                                width: "100%",
                                display: "flex",
                                justifyContent: "center",
                                pointerEvents: isOverflowing ? "auto" : "none",
                                opacity: isOverflowing ? 1 : 0,
                                transition: "opacity 180ms ease",
                            }}
                        >
                            <Typography
                                variant="body2"
                                color="primary"
                                sx={{
                                    cursor: isOverflowing ? "pointer" : "default",
                                    fontWeight: 500,
                                    background: "rgba(255,255,255,0.9)",
                                    px: 1,
                                    borderRadius: 1,
                                    userSelect: "none",
                                }}
                                onClick={isOverflowing ? handlePreviewCommentClick : undefined}
                            >
                                … See More
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Post Actions */}
                {/* Card typed posts can be liked and toggled for comments */}
                {/* Preview typed posts can be liked, but comments links to preview version of post. */}
                {type === "card" ? (
                    <Box display="flex" alignItems="center" gap={2} sx={{ pb: 2, pl: 2, pt: 1 }}>
                        <Box
                            onClick={() => handlePostLike()}
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{ cursor: "pointer", userSelect: "none" }}
                        >
                            {localPost.likes.some((username) => username === authState.account?.username) ? (
                                <Favorite sx={{ color: theme.palette.primary.main }} fontSize="small" />
                            ) : (
                                <FavoriteBorder fontSize="small" />
                            )}

                            <Typography variant="body2">{localPost.likes.length}</Typography>
                        </Box>
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{ cursor: "pointer", userSelect: "none" }}
                            onClick={toggleComments}
                        >
                            <ChatBubbleOutline fontSize="small" />
                            <Typography variant="body2">{localPost.comments.length}</Typography>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ width: "100%" }} display="flex" alignItems="center">
                        <Box
                            onClick={handlePostLike}
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{
                                cursor: "pointer",
                                userSelect: "none",
                                width: "50%",
                                alignItems: "center",
                                justifyContent: "center",
                                borderTop: "1px solid #E5E7EB",
                                borderRight: "1px solid #E5E7EB",
                                p: 1,
                                height: "40px",
                            }}
                        >
                            <Typography sx={{ mr: 0.5 }}>Like</Typography>
                            {localPost.likes.some((username) => username === authState.account?.username) ? (
                                <Favorite sx={{ color: theme.palette.primary.main }} fontSize="small" />
                            ) : (
                                <FavoriteBorder fontSize="small" />
                            )}

                            <Typography variant="body2">{localPost.likes.length}</Typography>
                        </Box>
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{
                                cursor: "pointer",
                                userSelect: "none",
                                width: "50%",
                                alignItems: "center",
                                justifyContent: "center",
                                borderTop: "1px solid #E5E7EB",
                                p: 1,
                                height: "40px",
                            }}
                            onClick={handlePreviewCommentClick}
                        >
                            <Typography sx={{ mr: 0.5 }}>Comment</Typography>
                            <ChatBubbleOutline fontSize="small" />
                            <Typography variant="body2">{localPost.comments.length}</Typography>
                        </Box>
                    </Box>
                )}
            </Box>

            {type === "card" && (
                <>
                    {showComments && (
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            sx={{
                                position: "sticky",
                                bottom: 0,
                                backgroundColor: "background.paper",
                                py: 1,
                                borderTop: "1px solid",
                                borderColor: "#E5E7EB",
                            }}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                placeholder={!!authState.account ? "Write a comment..." : "Please log in to comment..."}
                                value={newComment}
                                disabled={!authState.account}
                                onChange={(e) => setNewComment(e.target.value)}
                            />
                            <IconButton color="primary" onClick={() => handleCommentSubmit()} disabled={!newComment.trim()}>
                                {loading ? <Loading size="20px" /> : <Send />}
                            </IconButton>
                        </Box>
                    )}

                    {/* Comments Section */}
                    {showComments && localPost.comments.length > 0 && (
                        <Box>
                            <Divider sx={{ mb: 2 }} />

                            <Box sx={{ flexGrow: 1, pl: 6 }}>
                                <Comments comments={localPost.comments} setData={setPost ?? undefined} type="Post" />
                            </Box>
                        </Box>
                    )}
                </>
            )}

            <Dialog
                maxWidth="lg"
                fullWidth={!isXs}
                fullScreen={isXs}
                PaperProps={{
                    sx: {
                        width: "100vw",
                        height: "auto",
                        maxHeight: "100dvh",
                        m: 0,
                        borderRadius: 5,
                    },
                }}
                open={openImageModal || openRecipeModal || openCatalogListDialogModal}
                onClose={() => {
                    setOpenImageModal(false);
                    setOpenRecipeModal(false);
                    setOpenCatalogListDialogModal(false);
                }}
            >
                {openImageModal && (
                    <Box sx={{ height: { xs: "100dvh", sm: "80dvh" } }}>
                        <Box sx={{ position: "absolute", right: 0, top: 0, zIndex: 999 }}>
                            <IconButton
                                onClick={() => {
                                    setOpenImageModal(false);
                                }}
                            >
                                <Close />
                            </IconButton>
                        </Box>
                        <ImageBox
                            link={localPost.content.images.map((image) => "/post/" + localPost._id)}
                            images={localPost.content.images}
                            setImages={() => setLocalPostImages}
                            currentImageIndex={currentImageIndex}
                            setCurrentImageIndex={setCurrentImageIndex}
                        />
                    </Box>
                )}

                {openRecipeModal && currentRecipe !== null && (
                    <Box sx={{ width: "100%", maxWidth: "1200px", height: "100dvh", p: 3 }}>
                        <IconButton
                            onClick={() => {
                                setCurrentRecipe(null);
                                setOpenRecipeModal(false);
                            }}
                            sx={{ position: "absolute", zIndex: 999, top: 5, right: 5 }}
                        >
                            <Close />
                        </IconButton>
                        <SavedRecipe
                            recipeData={currentRecipe}
                            setRecipeData={setCurrentRecipe as React.Dispatch<React.SetStateAction<ISavedRecipes>>}
                        />
                    </Box>
                )}

                {openCatalogListDialogModal && (
                    <Box
                        sx={{
                            maxHeight: "90dvh",
                            height: { xs: "auto", sm: "auto" },
                            position: "relative",
                            overflow: { xs: "auto", sm: "none" },
                            ...scrollbarCss,
                        }}
                    >
                        {isXs && (
                            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                <IconButton onClick={() => setOpenCatalogListDialogModal(false)}>
                                    <Close />
                                </IconButton>
                            </Box>
                        )}
                        <CatalogListDialog postFollowedOnly={followedOnly} item={localPost} dialogType={"post"} setItem={setLocalPost} />
                    </Box>
                )}
            </Dialog>
        </Card>
    );
};

export default PostCard;
